import React from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  SettingsApplications as PartsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

const CarReplacementsSection = ({ 
  car, 
  showReplacements, 
  setShowReplacements, 
  replacements,
  isCarOwner,
  handleOpenAddReplacementDialog,
  handleOpenEditReplacementDialog,
  handleOpenDeleteReplacementDialog,
  getReplacementTypeIcon
}) => {
  const { t } = useTranslation('cars');
  const navigate = useNavigate();
  
  // Function to navigate to replacement detail page
  const handleReplacementClick = (replacement) => {
    navigate(`/cars/${car._id}/replacements/${replacement._id}`);
  };

  return (
    <Paper elevation={3}>
      <Box p={2}>
        <Box display="flex" alignItems="center" mb={showReplacements ? 2 : 0} onClick={() => setShowReplacements(!showReplacements)} sx={{ cursor: 'pointer' }}>
          <Box display="flex" alignItems="center">
            <PartsIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">{t('carDetail.replacements')}</Typography>
          </Box>
          <Box ml="auto" display="flex" alignItems="center">
            {isCarOwner() && showReplacements && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenAddReplacementDialog();
                }}
                sx={{ mr: 1, pr: 0 }}
              >
             
              </Button>
            )}
            {showReplacements ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
        </Box>
        
        <Collapse in={showReplacements}>
          {replacements.length > 0 ? (
            <List>
              {replacements.map((replacement, index) => (
                <React.Fragment key={replacement._id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem 
                    button 
                    onClick={() => handleReplacementClick(replacement)}
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        transform: 'translateY(-2px)',
                        boxShadow: 1
                      }
                    }}
                  >
                    <ListItemIcon>
                      {getReplacementTypeIcon(replacement.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Typography variant="subtitle1" fontWeight="medium">
                            {replacement.title}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {format(new Date(replacement.date), 'dd/MM/yyyy')}
                          </Typography>
                        </Box>
                      }
                    />
                    {isCarOwner() && (
                      <ListItemSecondaryAction>
                        <Tooltip title={t('common:actions.edit')}>
                          <IconButton 
                            edge="end" 
                            aria-label="edit" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEditReplacementDialog(replacement);
                            }}
                            size="small"
                            sx={{ mr: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common:actions.delete')}>
                          <IconButton 
                            edge="end" 
                            aria-label="delete" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDeleteReplacementDialog(replacement);
                            }}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box py={4} textAlign="center">
              <Typography variant="body1" color="text.secondary">
                {t('common:noData', 'No replacements found')}
              </Typography>
              {isCarOwner() && (
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddReplacementDialog}
                  sx={{ mt: 2 }}
                >
                  {t('cars:carDetail.addReplacement')}
                </Button>
              )}
            </Box>
          )}
        </Collapse>
      </Box>
    </Paper>
  );
};

export default CarReplacementsSection; 